import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Minus, Send, Loader2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useConversation, useDecryptedMessages, useSendMessage, type DecryptedMessage } from "@/hooks/useConversations";
import { mediaUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

// How far down a window needs to be dragged before releasing it counts as
// "throw to close" rather than just repositioning it - matches the
// intuitive weight of actually tossing something away, not an accidental
// small nudge.
const THROW_TO_CLOSE_DISTANCE = 120;

/** A quick floating side-chat isn't the place to fully render every message
 * type's rich UI (voice waveforms, poll bars, payment cards) the way the
 * full MessageBubble does - but it still needs to show *something* other
 * than the raw encrypted-then-decrypted JSON those types carry as their
 * plaintext (payment/poll/sticker/location/contact all decrypt to a JSON
 * string, not prose). This gives each one a short, honest label instead;
 * the real content is one tap away via the header link to the full chat. */
function formatMessagePreview(message: DecryptedMessage["message"], plaintext: string): string {
  if (message.message_type === "payment") return "Sent Skot";
  if (message.message_type === "payment_request") return "Requested Skot";
  if (message.message_type === "payment_split") return "Split a payment";
  if (message.message_type === "poll") return "Poll";
  if (message.message_type === "poll_vote") return "";
  if (message.message_type === "sticker") return "Sticker";
  if (message.message_type === "location") return "Location";
  if (message.message_type === "contact") return "Contact shared";
  if (plaintext.startsWith('{"type":"post_share"')) return "Shared a post";
  if (plaintext.startsWith('{"type":"story_reaction"')) {
    try {
      const emoji = (JSON.parse(plaintext) as { emoji?: string }).emoji;
      return emoji ? `${emoji} Reacted to your story` : "Reacted to your story";
    } catch {
      return "Reacted to your story";
    }
  }
  return plaintext;
}

export default function FloatingChatWindow({
  conversationId,
  offset,
  onClose
}: {
  conversationId: string;
  /** Which stacking slot this window occupies (0 = closest to the edge) -
   * positions windows side by side instead of on top of each other. */
  offset: number;
  onClose: () => void;
}) {
  const { data: conversation } = useConversation(conversationId);
  const { messages, isLoading } = useDecryptedMessages(conversationId);
  const sendMessage = useSendMessage(conversationId, conversation?.current_key_epoch ?? 1);
  const [minimized, setMinimized] = useState(false);
  const [text, setText] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{ startY: number; dragging: boolean } | null>(null);
  const [dragY, setDragY] = useState(0);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages.length]);

  async function handleSend() {
    const trimmed = text.trim();
    if (!trimmed) return;
    setText("");
    try {
      await sendMessage.mutateAsync({ content: trimmed });
    } catch {
      setText(trimmed);
    }
  }

  function handlePointerDown(e: React.PointerEvent) {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragState.current = { startY: e.clientY, dragging: true };
  }
  function handlePointerMove(e: React.PointerEvent) {
    if (!dragState.current?.dragging) return;
    setDragY(Math.max(0, e.clientY - dragState.current.startY));
  }
  function handlePointerUp() {
    if (dragState.current?.dragging && dragY > THROW_TO_CLOSE_DISTANCE) {
      onClose();
    }
    dragState.current = null;
    setDragY(0);
  }

  const name = conversation?.type === "direct" ? conversation.other_user?.display_name : conversation?.name;
  const avatarUrl = conversation?.type === "direct" ? conversation.other_user?.avatar_url : conversation?.avatar_url;

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="pointer-events-auto fixed bottom-4 z-40 h-14 w-14 overflow-hidden rounded-full border-2 border-background shadow-lg transition-transform hover:scale-105"
        style={{ right: `${16 + offset * 64}px` }}
        title={name ?? "Chat"}
      >
        <Avatar className="h-full w-full">
          <AvatarImage src={mediaUrl(avatarUrl)} />
          <AvatarFallback>{name?.[0] ?? "?"}</AvatarFallback>
        </Avatar>
      </button>
    );
  }

  return (
    <div
      className="pointer-events-auto fixed bottom-4 z-40 flex h-[420px] w-80 flex-col overflow-hidden rounded-xl border border-border bg-background shadow-2xl"
      style={{ right: `${16 + offset * 336}px`, transform: `translateY(${dragY}px)`, opacity: dragY > 0 ? Math.max(0.3, 1 - dragY / 300) : 1 }}
    >
      <div
        className="flex cursor-grab items-center gap-2 border-b border-border bg-secondary/40 p-2.5 active:cursor-grabbing"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <Link to={`/chat/${conversationId}`} className="flex min-w-0 flex-1 items-center gap-2" draggable={false}>
          <Avatar className="h-7 w-7 shrink-0">
            <AvatarImage src={mediaUrl(avatarUrl)} />
            <AvatarFallback>{name?.[0] ?? "?"}</AvatarFallback>
          </Avatar>
          <span className="truncate text-sm font-medium">{name ?? "Chat"}</span>
        </Link>
        <button onClick={() => setMinimized(true)} className="rounded p-1 text-muted-foreground hover:bg-secondary" title="Minimize">
          <Minus className="h-3.5 w-3.5" />
        </button>
        <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-secondary" title="Close">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div ref={listRef} className="flex-1 space-y-1.5 overflow-y-auto p-2.5">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : (
          messages
            .filter((m) => m.message.message_type !== "poll_vote")
            .map((m) => (
            <div
              key={m.message.id}
              className={cn("flex", m.message.sender_id === conversation?.other_user?.id ? "justify-start" : "justify-end")}
            >
              <div
                className={cn(
                  "max-w-[75%] rounded-2xl px-3 py-1.5 text-sm",
                  m.message.sender_id === conversation?.other_user?.id
                    ? "bg-secondary text-foreground"
                    : "bg-primary text-primary-foreground"
                )}
              >
                {m.plaintext
                  ? formatMessagePreview(m.message, m.plaintext)
                  : m.decryptionFailed
                    ? "Couldn't decrypt this message"
                    : "[attachment]"}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-border p-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
          placeholder="Message"
          className="flex-1 rounded-full border border-border bg-secondary/40 px-3 py-1.5 text-sm outline-none"
        />
        <button
          onClick={handleSend}
          disabled={!text.trim() || sendMessage.isPending}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-foreground text-background disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
